import React from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Slider,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { THEMES, getColorsFromConfig } from '../../Dashboard/AppThemeProvider';
import { setDetails, setTheme } from './appBuilderModel';

const HEADER_STYLES = [
  { id: 'flat', label: 'Clean', description: 'White bar with your color accents' },
  { id: 'gradient', label: 'Bold', description: 'Colored bar with a soft gradient' },
  { id: 'minimal', label: 'Minimal', description: 'Just the name, no bar' },
];

const CHAT_PLACEMENT = [
  { id: 'center', label: 'Inside the layout', description: 'The chat is one of the panels' },
  { id: 'header', label: 'Bar across the top', description: 'A search-style chat above everything' },
];

function Section({ title, hint, children }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {hint && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{hint}</Typography>}
      {!hint && <Box sx={{ mb: 1.5 }} />}
      {children}
    </Box>
  );
}

function ChoiceCard({
  selected, onClick, disabled, children, testId,
}) {
  return (
    <ButtonBase
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      data-testid={testId}
      sx={{
        p: 1.5,
        borderRadius: 2.5,
        border: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'rgba(37,99,235,.04)' : 'background.paper',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        textAlign: 'left',
        gap: 1,
      }}
    >
      {children}
    </ButtonBase>
  );
}

function HeaderSample({ style, color }) {
  const bar = {
    flat: { bgcolor: '#fff', borderBottom: '1px solid #e2e8f0' },
    gradient: { background: `linear-gradient(90deg, ${color}, ${color}cc)` },
    minimal: { bgcolor: 'transparent' },
  }[style];
  const dot = style === 'gradient' ? '#ffffffcc' : color;
  return (
    <Box sx={{
      height: 40, borderRadius: 1.5, overflow: 'hidden', bgcolor: '#f8fafc', border: 1, borderColor: 'divider',
    }}
    >
      <Box sx={{
        ...bar, height: 16, display: 'flex', alignItems: 'center', px: 0.75, gap: 0.5,
      }}
      >
        <Box sx={{
          width: 6, height: 6, borderRadius: '50%', bgcolor: dot,
        }}
        />
        <Box sx={{
          width: 28, height: 4, borderRadius: 1, bgcolor: dot, opacity: 0.7,
        }}
        />
      </Box>
    </Box>
  );
}

/** Plain-language "Look & feel" tab: name, colors, header, corners, chat placement. */
export default function AppLookPanel({ def, canEdit, onChange }) {
  const theme = def.theme && typeof def.theme === 'object' ? def.theme : {};
  const themeId = theme.themeId || theme.colorPreset || 'default';
  const colors = getColorsFromConfig(theme);
  const radius = typeof theme.borderRadius === 'number' ? theme.borderRadius : 8;
  const headerStyle = theme.headerStyle || 'flat';
  const chatPosition = def.chatPosition === 'header' ? 'header' : 'center';

  return (
    <Stack spacing={3.5}>
      <Section title="Name and description" hint="Shown at the top of the app and in your apps list.">
        <Stack spacing={1.5}>
          <TextField
            label="App name"
            size="small"
            fullWidth
            disabled={!canEdit}
            value={def.name || ''}
            onChange={(e) => onChange(setDetails(def, { name: e.target.value }))}
            inputProps={{ 'data-testid': 'app-name-input' }}
          />
          <TextField
            label="Short description"
            size="small"
            fullWidth
            multiline
            minRows={2}
            disabled={!canEdit}
            value={def.description || ''}
            onChange={(e) => onChange(setDetails(def, { description: e.target.value }))}
          />
        </Stack>
      </Section>

      <Section title="Colors" hint="Pick a palette. You can fine-tune the main color below.">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(92px, 1fr))', gap: 1 }}>
          {Object.entries(THEMES).map(([id, t]) => {
            const selected = themeId === id && !theme.primaryColor;
            return (
              <ButtonBase
                key={id}
                disabled={!canEdit}
                aria-pressed={selected}
                aria-label={`${t.name} colors`}
                data-testid={`theme-${id}`}
                onClick={() => onChange(setTheme(def, { themeId: id, primaryColor: undefined }))}
                sx={{
                  p: 1,
                  borderRadius: 2,
                  border: 2,
                  borderColor: selected ? 'primary.main' : 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 0.75,
                }}
              >
                <Box sx={{
                  display: 'flex', width: '100%', height: 28, borderRadius: 1.25, overflow: 'hidden', position: 'relative',
                }}
                >
                  <Box sx={{ flex: 2, bgcolor: t.primary }} />
                  <Box sx={{ flex: 1, bgcolor: t.secondary }} />
                  <Box sx={{ flex: 1, bgcolor: t.accent }} />
                  {selected && (
                    <CheckIcon sx={{
                      position: 'absolute', left: 6, top: 5, fontSize: 18, color: '#fff',
                    }}
                    />
                  )}
                </Box>
                <Typography variant="caption" fontWeight={600}>{t.name}</Typography>
              </ButtonBase>
            );
          })}
        </Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1.5 }}>
          <Tooltip title="Main color">
            <Box
              component="input"
              type="color"
              disabled={!canEdit}
              value={/^#[0-9a-f]{6}$/i.test(colors.primary) ? colors.primary : '#1976d2'}
              onChange={(e) => onChange(setTheme(def, { primaryColor: e.target.value }))}
              aria-label="Main color"
              sx={{
                width: 40, height: 40, p: 0, border: 0, borderRadius: 1.5, cursor: 'pointer', bgcolor: 'transparent',
              }}
            />
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>Main color</Typography>
            <Typography variant="caption" color="text.secondary">
              {theme.primaryColor ? `Custom (${theme.primaryColor})` : `From the ${THEMES[themeId]?.name || 'selected'} palette`}
            </Typography>
          </Box>
          {theme.primaryColor && canEdit && (
            <Button size="small" onClick={() => onChange(setTheme(def, { primaryColor: undefined }))} sx={{ textTransform: 'none' }}>
              Use palette color
            </Button>
          )}
        </Stack>
      </Section>

      <Section title="Header">
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1.25 }}>
          {HEADER_STYLES.map((h) => (
            <ChoiceCard
              key={h.id}
              selected={headerStyle === h.id}
              disabled={!canEdit}
              testId={`header-${h.id}`}
              onClick={() => onChange(setTheme(def, { headerStyle: h.id }))}
            >
              <HeaderSample style={h.id} color={colors.primary} />
              <Box>
                <Typography variant="body2" fontWeight={700}>{h.label}</Typography>
                <Typography variant="caption" color="text.secondary">{h.description}</Typography>
              </Box>
            </ChoiceCard>
          ))}
        </Box>
      </Section>

      <Section title="Corners" hint="How rounded buttons, cards and panels look.">
        <Stack direction="row" spacing={2.5} alignItems="center" sx={{ px: 1 }}>
          <Box sx={{
            width: 36, height: 36, border: 2, borderColor: colors.primary, borderRadius: `${radius}px`, flex: '0 0 auto',
          }}
          />
          <Slider
            value={radius}
            min={0}
            max={24}
            step={2}
            disabled={!canEdit}
            marks={[{ value: 0, label: 'Square' }, { value: 12, label: 'Soft' }, { value: 24, label: 'Round' }]}
            onChange={(_, v) => onChange(setTheme(def, { borderRadius: v }))}
            aria-label="Corner roundness"
          />
        </Stack>
      </Section>

      <Section title="Logo" hint="Optional. Paste a link to a PNG or SVG.">
        <TextField
          size="small"
          fullWidth
          disabled={!canEdit}
          placeholder="https://example.com/logo.svg"
          value={theme.logoUrl || ''}
          onChange={(e) => onChange(setTheme(def, { logoUrl: e.target.value.trim() }))}
        />
      </Section>

      <Section title="Where the chat sits">
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
          {CHAT_PLACEMENT.map((c) => (
            <ChoiceCard
              key={c.id}
              selected={chatPosition === c.id}
              disabled={!canEdit}
              testId={`chat-position-${c.id}`}
              onClick={() => onChange(setDetails(def, { chatPosition: c.id }))}
            >
              <Typography variant="body2" fontWeight={700}>{c.label}</Typography>
              <Typography variant="caption" color="text.secondary">{c.description}</Typography>
            </ChoiceCard>
          ))}
        </Box>
      </Section>
    </Stack>
  );
}
